// src/context/TraumatoCatalogContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../utils/apiFetch';
import { CONFIG } from '../config/config';

const API_BASE = (CONFIG?.API_URL || process.env.REACT_APP_API_URL || '/api').replace(/\/+$/, '');

// lecture unifiée du token (same tab)
const readToken = () =>
  (typeof window !== 'undefined' &&
    (sessionStorage.getItem('token') ||
      localStorage.getItem('token') ||
      (document.cookie.match(/(?:^|;)\s*token=([^;]+)/)?.[1] ?? null))) ||
  null;

const Ctx = createContext(null);
const norm = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

export function TraumatoCatalogProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [catalog, setCatalog] = useState({ categories: [] });
  const [authToken, setAuthToken] = useState(readToken());

  // ✅ Observe le token dans le *même onglet* et sur focus
  useEffect(() => {
    const onStorage = () => setAuthToken(readToken());
    const onFocus = () => setAuthToken(readToken());
    const ping = setInterval(() => {
      // couvre le cas où le token est set sans navigation
      setAuthToken((t) => {
        const now = readToken();
        return t === now ? t : now;
      });
    }, 700);
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(ping);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // (re)charge le catalogue dès que le token est dispo
  useEffect(() => {
    let aborted = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        if (!authToken) {
          setError('Token manquant');
          setCatalog({ categories: [] });
          return;
        }
        // apiFetch ajoutera Authorization avec le token courant
        const res = await apiFetch('/api/traumato/catalog');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!aborted) setCatalog(json || { categories: [] });
      } catch (e) {
        if (!aborted) setError(e?.message || 'Erreur de chargement.');
        console.error('TraumatoCatalog load error:', e);
      } finally {
        if (!aborted) setLoading(false);
      }
    }; // <- ✅ ferme la fonction load()

    load(); // lance le chargement
    return () => {
      aborted = true;
    };
  }, [authToken]);

  const actsFlat = useMemo(() => {
    const out = [];
    for (const c of catalog.categories || []) {
      if (Array.isArray(c.acts))
        for (const a of c.acts) out.push({ ...a, _cat: c.name, _sub: null });
      else if (Array.isArray(c.subCategories))
        for (const s of c.subCategories)
          for (const a of s.acts || []) out.push({ ...a, _cat: c.name, _sub: s.name });
    }
    return out;
  }, [catalog]);

  const categories = useMemo(() => (catalog.categories || []).map((c) => c.name), [catalog]);

  const categoryByNorm = useMemo(() => {
    const map = new Map();
    for (const c of catalog.categories || []) if (c?.name) map.set(norm(c.name), c);
    return map;
  }, [catalog]);

  const getActsByCategory = React.useCallback(
    (catName) => {
      const cat = categoryByNorm.get(norm(catName));
      if (!cat) return [];
      if (Array.isArray(cat.acts))
        return (cat.acts || []).map((a) => ({ ...a, _cat: cat.name, _sub: null }));
      return actsFlat.filter((a) => norm(a._cat) === norm(cat.name));
    },
    [categoryByNorm, actsFlat],
  );

  const getSubCategoriesByCategory = React.useCallback(
    (catName) => {
      const cat = categoryByNorm.get(norm(catName));
      if (!cat) return [];
      if (Array.isArray(cat.subCategories)) return cat.subCategories.map((s) => s.name);
      return [];
    },
    [categoryByNorm],
  );

  const api = useMemo(
    () => ({
      loading,
      error,
      catalog,
      categories,
      actsFlat,
      getActsByCategory,
      getSubCategoriesByCategory,
      getActsForSubCategory: (sub) => actsFlat.filter((a) => a._sub === sub),
      reload: () => setAuthToken(readToken()),
    }),
    [loading, error, catalog, categories, actsFlat, getActsByCategory, getSubCategoriesByCategory],
  );

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useTraumatoCatalog() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTraumatoCatalog must be used within TraumatoCatalogProvider');
  return ctx;
}
